import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { useTenant } from '../TenantContext';
import { Attribute } from '../../types';
import { AID_CALCULATION_OPTIONS } from '../../constants';
import { determinePreheaderText, determineFooterText } from '../../utils/summaryHeaderFooterLogic';
import { extractAndStoreNeedBasedGrant } from '../../utils/needBasedGrantConditional';
import './PrintPreview.css';

// Add print styles to ensure content shows in print preview
const printStyles = `
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    font-size: 12px;
    line-height: 1.4;
    color: #000;
  }
  
  .print-content {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 20mm !important;
    background: white !important;
    color: black !important;
  }
  

  .bg-red-600 { background-color: #990050 !important; }
  
  /* Ensure logo background prints correctly */
  .bg-red-600, 
  div[style*="backgroundColor: ''"],
  div[style*="background-color: "] { 
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Header row print styles */
  div[style*="height: 65px"][style*="width: 595px"] {
    height: 65px !important;
    width: 595px !important;
  }
  
  div[style*="marginTop: 34px"] {
    margin-top: 34px !important;
  }

  .bg-gray-100 { background-color: #f3f4f6 !important; }
  .text-white { color: white !important; }
  .text-gray-600 { color: #4b5563 !important; }
  .text-gray-700 { color: #374151 !important; }
  .text-gray-800 { color: #1f2937 !important; }
  .text-gray-900 { color: #111827 !important; }
  .border-gray-200 { border-color: #e5e7eb !important; }
  .border-gray-400 { border-color: #9ca3af !important; }
  
  /* Page counter functionality */
  @page {
    @bottom-right {
      content: counter(page) "/" counter(pages);
      font-size: 10px;
    }
  }
  
  .page-counter::after {
    content: counter(page) "/" counter(pages);
  }
}
`;

// Simple currency formatter
const formatCurrency = (
    value: string | number | null | undefined,
    fieldName?: string,
    hasOptedOut?: boolean,
    attributeTypeId?: string,
    estimatedAdditionalFundsOperandIds?: Set<string>
): string => {
    // Check if this is Estimated Additional Funds or its operands when user has opted out
    if (hasOptedOut && (fieldName === 'Estimated Additional Funds' || (attributeTypeId && estimatedAdditionalFundsOperandIds?.has(attributeTypeId)))) {
        return 'N/A';
    }
    
    // Show $0 for Need-Based Grant Estimate when user has opted out
    if (hasOptedOut && fieldName === 'Need-Based Grant Estimate') {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(0);
    }

    if (value === null || value === undefined) return 'N/A';

    // Handle pre-formatted currency strings by extracting the first valid number (with optional leading minus and decimal)
    const numValue = typeof value === 'string'
        ? (() => {
            // Only accept strings that are valid numbers (optional leading minus, digits, optional single decimal point)
            const trimmed = value.trim();
            const validNumberPattern = /^-?\d+(\.\d+)?$/;
            return validNumberPattern.test(trimmed) ? Number.parseFloat(trimmed) : NaN;
        })()
        : value;

    if (Number.isNaN(numValue)) return 'N/A';

    // Show N/A for Scout Award when value is $0
    if (fieldName === 'Scout Award' && numValue === 0) {
        return 'N/A';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(numValue);
};

const shouldHideValue = (value: string): boolean => {
    return value === 'N/A' || value === '$0';
};

interface PrintPreviewProps {
    isOpen: boolean;
    onClose: () => void;
    derivedAttributesData?: { objects: { attributes: Attribute[] }[] } | null;
}

const PrintPreview: React.FC<PrintPreviewProps> = ({ isOpen, onClose, derivedAttributesData }) => {
    const { getAttributesByEntityName, tenantConfig, loading } = useTenant();
    const overlayRef = useRef<HTMLDivElement>(null);

    // Get data from tenantConfig or fall back to localStorage
    const logoUrl = tenantConfig?.authority?.authorityBranding?.logoUrl ?? localStorage.getItem('TENANT_LOGO') ?? '';
    const fullName = tenantConfig?.authority?.displayName ?? localStorage.getItem('TENANT_FULLNAME') ?? '';



    // Get today's date and time in mm/dd/yy format with hh:mm AM/PM
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit'
    });
    const formattedTime = today.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
    const dateTimeString = `${formattedDate} ${formattedTime}`;

    // Check if user opted out of need-based aid
    const hasOptedOut = useMemo(() => {
        const familyInfoObject = derivedAttributesData?.objects?.find((obj: any) => obj.name === 'FamilyInformation');
        const familyAttributes = familyInfoObject?.attributes || [];

        // Check by friendlyName first, then by name as fallback
        const aidPreferenceAttr = familyAttributes.find((attr: any) =>
            attr.friendlyName === 'student_aid_preference' ||
            (attr.name?.toLowerCase().includes('aid') && attr.name?.toLowerCase().includes('preference'))
        );

        if (!aidPreferenceAttr) return false;

        const selectedValue = aidPreferenceAttr.value ?? '';
        // Check for combined value OR individual values
        return selectedValue === 'I do not want to calculate need-based financial aid; OR I am an international citizen ineligible for need-based financial aid' ||
            selectedValue === AID_CALCULATION_OPTIONS.SKIP_INTERNATIONAL ||
            selectedValue === AID_CALCULATION_OPTIONS.SKIP_NO_NEED;
    }, [derivedAttributesData]);

    // Build dynamic print sections from API data (same logic as EstimatedPrice.tsx)
    const printSections = useMemo(() => {
        const initialEstimateAttributes = getAttributesByEntityName('Estimate');
        const allDerivedAttributes = derivedAttributesData?.objects?.flatMap(obj => obj.attributes) || [];

        const attributeMap = new Map<string, Attribute>();

        [...allDerivedAttributes, ...initialEstimateAttributes].forEach(attr => {
            if (attr.attributeTypeId) {
                attributeMap.set(attr.attributeTypeId, attr);
            }
        });

        const getAttributeValue = (attr: Attribute | undefined): string | number => {
            if (!attr) return 'N/A';
            if (attr.value !== null && attr.value !== undefined && !attr.isNa) {
                return attr.value;
            }
            return attr.constantValue ?? attr.defaultValue ?? '0';
        };

        const getAttributeValueWithOperand = (attr: Attribute, operand?: any): string => {
            if (operand?.isNa) return 'N/A';
            if (operand?.value !== null && operand?.value !== undefined && operand?.value !== 'NULL') {
                return String(operand.value);
            }
            return String(getAttributeValue(attr));
        };

        // Recursive helper to get Details from grandchild operands
        const getChildDetails = (childAttr: Attribute, estimatedAdditionalFundsOperandIds: Set<string>): any[] => {
            if (!childAttr.operands || childAttr.operands.length === 0) {
                return [];
            }

            return childAttr.operands
                .map((grandchildOperand: any) => {
                    const grandchildAttr = attributeMap.get(grandchildOperand.attributeId);
                    if (!grandchildAttr?.name) return null;

                    const value = getAttributeValueWithOperand(grandchildAttr, grandchildOperand);
                    const formattedCost = formatCurrency(value, grandchildAttr.name, hasOptedOut, grandchildAttr.attributeTypeId, estimatedAdditionalFundsOperandIds);

                    // Hide individual detail items with $0 or N/A values (e.g., Virtual Session, Food, Loan Fees, Fees)
                    if (shouldHideValue(formattedCost)) {
                        return null;
                    }

                    return {
                        item: grandchildAttr.name,
                        cost: formattedCost,
                        order: grandchildAttr.order ?? 999
                    };
                })
                .filter((detail): detail is any => detail !== null);
        };

        // Recursive helper to get SubSections from child operands
        const getChildSections = (parentAttr: Attribute, estimatedAdditionalFundsOperandIds: Set<string>): any[] => {
            if (!parentAttr.operands || parentAttr.operands.length === 0) {
                return [];
            }

            return parentAttr.operands
                .map((childOperand: any) => {
                    const childAttr = attributeMap.get(childOperand.attributeId);
                    if (!childAttr?.name) return null;

                    const value = getAttributeValueWithOperand(childAttr, childOperand);
                    const details = getChildDetails(childAttr, estimatedAdditionalFundsOperandIds);

                    return {
                        label: childAttr.name,
                        price: formatCurrency(value, childAttr.name, hasOptedOut, childAttr.attributeTypeId, estimatedAdditionalFundsOperandIds),
                        details,
                        order: childAttr.order ?? 999
                    };
                })
                .filter((subsection): subsection is any => subsection !== null)
                .sort((a, b) => a.order - b.order);
        };

        const sections: any[] = [];
        const processedAttributes = new Set<string>();

        // Only hide Direct Costs and Indirect Costs (children of Cost of Attendance)
        // and their nested children (Tuition, Fees, Room & Board, etc.)
        const costOfAttendanceAttr = initialEstimateAttributes.find(attr => attr.name === 'Cost Of Attendance');
        const estimatedAdditionalFundsAttr = initialEstimateAttributes.find(attr => attr.name === 'Estimated Additional Funds');
        const childAttributeIds = new Set<string>();
        const estimatedAdditionalFundsOperandIds = new Set<string>();

        if (costOfAttendanceAttr?.operands) {
            costOfAttendanceAttr.operands.forEach((operand: any) => {
                // Add Direct Costs and Indirect Costs
                childAttributeIds.add(operand.attributeId);

                // Add their nested children (Tuition, Fees, Room & Board, Books/Supplies, etc.)
                const childAttr = attributeMap.get(operand.attributeId);
                if (childAttr?.operands && childAttr.operands.length > 0) {
                    childAttr.operands.forEach((subOperand: any) => {
                        childAttributeIds.add(subOperand.attributeId);
                    });
                }
            });
        }

        // Track Estimated Additional Funds operands dynamically from the API response
        if (estimatedAdditionalFundsAttr?.operands && estimatedAdditionalFundsAttr.operands.length > 0) {
            estimatedAdditionalFundsAttr.operands.forEach((operand: any) => {
                if (operand.attributeId) {
                    estimatedAdditionalFundsOperandIds.add(operand.attributeId);
                }
            });
        }

        // Filter to get all estimate attributes except those nested under Cost of Attendance
        const estimateAttributes = initialEstimateAttributes.filter(attr =>
            attr.name &&
            attr.attributeTypeId &&
            !childAttributeIds.has(attr.attributeTypeId)
        );

        // Process top-level attributes
        estimateAttributes.forEach((attr) => {
            if (!attr.attributeTypeId || processedAttributes.has(attr.attributeTypeId)) {
                return;
            }

            processedAttributes.add(attr.attributeTypeId);

            // Create hierarchical subsections for Cost of Attendance
            const subSections = attr.name === 'Cost Of Attendance'
                ? getChildSections(attr, estimatedAdditionalFundsOperandIds)
                : [];

            const value = getAttributeValue(attr);
            const formattedPrice = formatCurrency(value, attr.name, hasOptedOut, attr.attributeTypeId, estimatedAdditionalFundsOperandIds);

            // Determine if this section should be bold (matching sidebar logic)
            const isKeySummaryAttribute = ['Cost Of Attendance', 'Merit Scholarship Estimate', 'Need-Based Grant Estimate', 'Estimated Net Price', 'Estimated Additional Funds'].includes(attr.name ?? '');
            const shouldBeBold = isKeySummaryAttribute;

            // Check if this is an operand of Estimated Additional Funds
            const isEstimatedAdditionalFundsOperand = attr.attributeTypeId ? estimatedAdditionalFundsOperandIds.has(attr.attributeTypeId) : false;

            sections.push({
                name: attr.name ?? '',
                value: formattedPrice,
                order: attr.order ?? 999,
                hasBreakdown: subSections.length > 0,
                children: subSections,
                bold: shouldBeBold,
                isEstimatedAdditionalFundsOperand
            });
        });

        // Filter out sections, children, and details with N/A or $0 values
        // Exception: Merit Scholarship Estimate and Need-Based Grant Estimate always show, even when $0
        const isEstimateDisplayAlways = (name: string) =>
            name === 'Merit Scholarship Estimate' || name === 'Need-Based Grant Estimate';

        const filteredSections = sections
            .filter(section => section.value !== 'N/A' && (section.value !== '$0' || isEstimateDisplayAlways(section.name)))
            .map(section => ({
                ...section,
                children: section.hasBreakdown && section.children
                    ? section.children
                        .filter((child: any) => child.price !== 'N/A' && child.price !== '$0')
                        .map((child: any) => ({
                            ...child,
                            details: child.details
                                ? child.details.filter((detail: any) => detail.cost !== 'N/A' && detail.cost !== '$0')
                                : []
                        }))
                    : []
            }));

        return filteredSections.sort((a, b) => a.order - b.order);
    }, [derivedAttributesData, getAttributesByEntityName, hasOptedOut]);

    // Get estimated net price for header (raw value, not formatted)
    const estimatedNetPrice = useMemo(() => {
        const initialEstimateAttributes = getAttributesByEntityName('Estimate');
        const allDerivedAttributes = derivedAttributesData?.objects?.flatMap(obj => obj.attributes) || [];

        const attr = allDerivedAttributes.find(attr => attr.name === 'Estimated Net Price')
            || initialEstimateAttributes.find(attr => attr.name === 'Estimated Net Price');

        if (attr?.value !== null && attr?.value !== undefined && !attr?.isNa) {
            return attr.value;
        }
        return attr?.constantValue ?? attr?.defaultValue ?? '0';
    }, [derivedAttributesData, getAttributesByEntityName]);

    // Get Summary attributes for dynamic text
    const summaryAttributes = useMemo(() => {
        const attrs = getAttributesByEntityName('Summary');
        return attrs;
    }, [getAttributesByEntityName]);

    // All derived attributes flattened (same as ControlRender summary tab)
    const allDerivedAttributes = useMemo(() => {
        return derivedAttributesData?.objects?.flatMap(obj => obj.attributes) || [];
    }, [derivedAttributesData]);

    // Sorted summary attributes (same order as ControlRender summary tab)
    const sortedSummaryAttrs = useMemo(() => {
        return [...summaryAttributes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [summaryAttributes]);

    // Need-based grant value – populate localStorage then read it back (mirrors ControlRender)
    const needBasedGrantValue = useMemo(() => {
        extractAndStoreNeedBasedGrant(allDerivedAttributes);
        return Number.parseFloat(localStorage.getItem('needBasedGrantEstimate') || '0');
    }, [allDerivedAttributes]);

    // Raw disclaimer text from empty-name, long-description attribute – passed to determineFooterText
    const disclaimerTextRaw = useMemo(() => {
        const attr = sortedSummaryAttrs.find(a =>
            (!a.name || a.name.trim() === '') && a.description && a.description.length > 100
        );
        return attr?.description || '';
    }, [sortedSummaryAttrs]);

    // Header note text – uses same multi-tenant logic as the summary tab (ControlRender)
    const headerNote = useMemo(() => {
        const { headerNote: note } = determinePreheaderText(
            allDerivedAttributes,
            sortedSummaryAttrs,
            derivedAttributesData as any,
            needBasedGrantValue,
            tenantConfig?.authority?.displayName ?? ''
        );
        if (!note?.trim()) return '';
        // Intelligently shorten link text: replace with "here" only when preceded by "Click"
        return note.replace(/<a\s{1,100}([^>]{0,2000})>([^<]{1,2000})<\/a>/g, (match: string, attrs: string, linkText: string) => {
            const textBeforeLink = note.substring(0, note.indexOf(match)).toLowerCase();
            const wordsBeforeLink = textBeforeLink.trim().split(/\s+/).slice(-2);
            const hasClickWord = wordsBeforeLink.some((word: string) => word.replace(/[.,;:!?]/g, '') === 'click');
            return hasClickWord ? `<a ${attrs}>here</a>` : `<a ${attrs}>${linkText}</a>`;
        });
    }, [allDerivedAttributes, sortedSummaryAttrs, derivedAttributesData, needBasedGrantValue, tenantConfig]);

    const processDisclaimerHtmlText = (rawText: string): string => {
        let processedText = rawText.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
        processedText = processedText.replace(/<a\s{1,100}([^>]{0,2000})>([^<]{1,2000})<\/a>/g, (match: string, attrs: string, linkText: string) => {
            const textBeforeLink = processedText.substring(0, processedText.indexOf(match)).toLowerCase();
            const wordsBeforeLink = textBeforeLink.replace(/<br>/g, ' ').trim().split(/\s+/).slice(-2);
            const hasClickWord = wordsBeforeLink.some((word: string) => word.replace(/[.,;:!?]/g, '') === 'click');
            return hasClickWord ? `<a ${attrs}>this link</a>` : `<a ${attrs}>${linkText}</a>`;
        });
        return processedText;
    };

    // Footer result – uses same multi-tenant logic as the summary tab (ControlRender)
    const { footerTextToRender: rawFooterText, showWorkStudyFooter, workStudyFooterText } = useMemo(() => {
        return determineFooterText(
            allDerivedAttributes,
            sortedSummaryAttrs,
            derivedAttributesData as any,
            disclaimerTextRaw,
            needBasedGrantValue,
            tenantConfig?.authority?.displayName ?? ''
        );
    }, [allDerivedAttributes, sortedSummaryAttrs, derivedAttributesData, disclaimerTextRaw, needBasedGrantValue, tenantConfig]); // eslint-disable-line react-hooks/exhaustive-deps

    // Processed disclaimer text derived from multi-tenant footer logic
    const disclaimerText = useMemo(() => {
        if (!rawFooterText) return '';
        return processDisclaimerHtmlText(rawFooterText);
    }, [rawFooterText]); // eslint-disable-line react-hooks/exhaustive-deps

    // Extract ALL special text between #* and *# delimiters for display above "Connect with Us"
    const specialFooterText = useMemo(() => {
        if (!disclaimerText) return '';

        // Collect every #* ... *# section
        const matches: string[] = [];
        const regex = /#\*([\s\S]*?)\*#/g;
        let m;
        while ((m = regex.exec(disclaimerText)) !== null) {
            const trimmed = m[1].trim();
            if (trimmed) matches.push(trimmed);
        }

        if (matches.length === 0) return '';

        // Join sections with a line break, then convert raw URLs to clickable links
        const combined = matches.join('<br>');
        return combined.replace(/(?<![="'])(https?:\/\/[^\s<>"')\]]+)/g,
            (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: underline;">${url}</a>`);
    }, [disclaimerText]);

    // Clean disclaimer text by removing the #* *# section
    const cleanedDisclaimerText = useMemo(() => {
        if (!disclaimerText) return '';

        // Remove text between #* and *# delimiters (including the delimiters)
        return disclaimerText.replace(/#\*.*?\*#/gs, '').trim();
    }, [disclaimerText]);

    const handlePrint = useCallback(() => {
        // Trigger browser print dialog directly on current page
        try {
            // Use browser's native print function
            window.print();
            // Close modal after print dialog
            setTimeout(() => {
                onClose();
            }, 100);
        } catch (error) {
            console.error('Print failed:', error);
            onClose(); // Close on error too
        }
    }, [onClose]);

    // Handle background page resizing to match print modal size
    useEffect(() => {
        if (isOpen) {
            // Disable scrolling on body
            document.body.style.overflow = 'hidden';

            const body = document.body;

            // Store original styles
            const originalStyles = {
                bodyTransform: body.style.transform || '',
                bodyTransformOrigin: body.style.transformOrigin || '',
                bodyWidth: body.style.width || '',
                bodyHeight: body.style.height || '',
                bodyPosition: body.style.position || '',
                bodyTop: body.style.top || '',
                bodyLeft: body.style.left || ''
            };
            

            // Keep background page at normal size since modal covers full viewport
            body.style.transform = 'scale(1)';
            body.style.transformOrigin = 'top left';
            body.style.width = '100%';
            body.style.height = '100%';
            body.style.position = 'fixed';
            body.style.top = '0';
            body.style.left = '0';
            

            return () => {
                // Restore original styles when modal closes
                document.body.style.overflow = 'auto';
                body.style.transform = originalStyles.bodyTransform;
                body.style.transformOrigin = originalStyles.bodyTransformOrigin;
                body.style.width = originalStyles.bodyWidth;
                body.style.height = originalStyles.bodyHeight;
                body.style.position = originalStyles.bodyPosition;
                body.style.top = originalStyles.bodyTop;
                body.style.left = originalStyles.bodyLeft;
            };
        }
    }, [isOpen]);

    // Inject print styles and auto-trigger print when modal opens
    useEffect(() => {
        if (isOpen) {
            // Inject print styles
            const styleElement = document.createElement('style');
            styleElement.textContent = printStyles;
            document.head.appendChild(styleElement);
            

            // Small delay to ensure content is rendered, then auto-print
            const timer = setTimeout(() => {
                handlePrint();
            }, 300);
            

            return () => {
                clearTimeout(timer);
                // Clean up styles
                if (document.head.contains(styleElement)) {
                    document.head.removeChild(styleElement);
                }
            };
        }
    }, [isOpen, handlePrint]);

    // Calculate pages when modal opens or content changes

    if (!isOpen) return null;

    if (loading) {
        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-50 print:hidden flex items-center justify-center"
                role="button"
                tabIndex={0}
                aria-label="Close"
                onClick={onClose}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') onClose(); }}
            >
                <div className="bg-white p-8 rounded-lg shadow-lg">
                    <div className="flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="text-gray-700">Loading tenant data...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Print modal overlay - visible on current page */}
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-white z-50"
                role="button"
                tabIndex={0}
                aria-label="Close"
                onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') onClose(); }}
                style={{
                    width: '100vw',
                    height: '100vh',
                    overflow: 'auto',
                    cursor: 'pointer'
                }}
            >
                <div className="bg-white w-full h-full overflow-y-auto max-w-4xl mx-auto">

                    {/* Print content - A4 format - Direct modal view */}
                    <div className="print-content bg-white" style={{
                        width: '210mm',
                        minHeight: '297mm',
                        margin: '0 auto',
                        padding: '20mm',
                        boxSizing: 'border-box',
                        transform: 'scale(1)',
                        transformOrigin: 'top center',
                        lineHeight: '1.4'
                    }}>
                        {/* Header section - simplified without box */}
                        <div className="mb-6">
                            {/* Date section */}
                            <div className="text-left mb-2">
                                <p className="text-xs text-gray-600">
                                    {dateTimeString}
                                </p>
                            </div>
                            

                            {/* Header row with logo and title */}
                            <div className="mb-4" style={{ marginTop: '34px' }}>
                                <div className="flex items-center gap-4">
                                    {logoUrl && (
                                        <div className="flex-shrink-0 flex items-center justify-center">
                                            <img
                                                src={logoUrl}
                                                alt={`${fullName} Logo`}
                                                style={{ maxWidth: '100px', maxHeight: '60px', width: 'auto', height: 'auto', objectFit: 'contain' }}
                                                onError={(e) => {
                                                    console.error('Logo failed to load:', logoUrl);
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                }}
                                                onLoad={() => {
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex flex-col justify-center">
                                        <h1 className="text-lg font-semibold text-gray-800 leading-tight">
                                            {fullName} Net Price Calculator
                                        </h1>
                                        <p className="text-xl font-bold text-gray-900 mt-1">
                                            Your Estimated Net Price: {formatCurrency(estimatedNetPrice, 'Estimated Net Price', hasOptedOut)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Horizontal line */}
                            <hr className="border-t border-gray-400 mb-4" />

                            {/* Header note – tenant-specific preheader (same logic as summary tab) */}
                            {headerNote && (
                                <div className="text-xs text-gray-700 mb-3" style={{ fontSize: '9px' }}>
                                    <div dangerouslySetInnerHTML={{ __html: headerNote }} />
                                </div>
                            )}
                        </div>

                        {/* Main financial breakdown table - dynamically rendered from API */}
                        <div className="space-y-0">
                            {printSections.map((section, index) => {
                                const isEstimatedNetPrice = section.name === 'Estimated Net Price';

                                // Find the index of the last Estimated Additional Funds operand
                                const lastOperandIndex = printSections.reduce((lastIdx, s, i) =>
                                    s.isEstimatedAdditionalFundsOperand ? i : lastIdx, -1
                                );
                                const isLastOperand = section.isEstimatedAdditionalFundsOperand && index === lastOperandIndex;

                                // Calculate padding to match sidebar spacing (print version uses more compact spacing)
                                let paddingClass = 'pt-2 pb-1'; // default - more compact for print with reduced bottom padding
                                if (section.name === 'Estimated Additional Funds') {
                                    paddingClass = 'pt-2 pb-0';
                                } else if (section.isEstimatedAdditionalFundsOperand) {
                                    paddingClass = isLastOperand ? 'pt-2 pb-4' : 'pt-2 pb-0';
                                }

                                return (
                                    <div key={section.name}>
                                        {/* Main section */}
                                        <div className={`flex justify-between items-center px-4 ${paddingClass}`}>
                                            <h3 className={`${section.bold ? "font-bold" : "font-medium"} ${isEstimatedNetPrice ? "text-base" : "text-sm"}`}>
                                                {section.name}
                                            </h3>
                                            <span className={`${section.bold ? "font-bold" : "font-medium"} ${isEstimatedNetPrice ? "text-base" : "text-sm"}`}>
                                                {section.value}
                                            </span>
                                        </div>

                                        {/* Child sections (e.g., Direct Costs, Indirect Costs) */}
                                        {section.hasBreakdown && section.children && section.children.length > 0 && (
                                            <div className="bg-white">
                                                {section.children.map((child: any, childIndex: number) => (
                                                    <div key={child.label}>
                                                        <div className="flex justify-between px-4 pt-2">
                                                            <span className="text-xs font-bold">{child.label}</span>
                                                            <span className="font-bold text-xs">{child.price}</span>
                                                        </div>

                                                        {/* Details (e.g., Tuition, Fees under Direct Costs) */}
                                                        {child.details && child.details.length > 0 && (
                                                            <div className="pl-4">
                                                                {child.details.map((detail: any) => (
                                                                    <div key={detail.item} className="flex justify-between pr-4 py-0.5 text-xs">
                                                                        <span>{detail.item}</span>
                                                                        <span>{detail.cost}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Horizontal line */}
                            <hr className="border-t border-gray-400" />
                        </div>

                        {/* Bottom section */}
                        <div className="mt-4 space-y-2">
                            {/* Special footer text from #* *# delimiters - shown above Connect with Us */}
                            {specialFooterText && (
                                <div className="text-xs text-gray-700 leading-relaxed text-center">
                                    <div dangerouslySetInnerHTML={{ __html: specialFooterText }} />
                                </div>
                            )}

                            {/* Connect with Us */}
                            <div className="text-center">
                                <p className="text-sm font-semibold">
                                    <strong>Connect with Us:</strong> {localStorage.getItem('CONTACT_URL') || 'https://humancapital.com/contact-us/'}
                                </p>
                            </div>

                            {/* Horizontal line under Connect with Us */}
                            <hr className="border-t border-gray-400" />

                            {/* Work-study footer – shown conditionally per tenant (e.g. Loyola, Tampa) */}
                            {showWorkStudyFooter && workStudyFooterText && (
                                <div className="text-xs text-gray-700 leading-relaxed mb-2">
                                    <div dangerouslySetInnerHTML={{ __html: workStudyFooterText.replaceAll('\n', '<br>') }} />
                                </div>
                            )}

                            {/* Disclaimer text – tenant-specific footer (with #* *# section removed) */}
                            {cleanedDisclaimerText && (
                                <div className="text-xs text-gray-700 leading-relaxed">
                                    <div dangerouslySetInnerHTML={{ __html: cleanedDisclaimerText }} />
                                </div>
                            )}

                            {/* Horizontal line after last paragraph */}
                            <hr className="border-t border-gray-400" />

                            {/* Address section with logo */}
                            <div className="flex items-center justify-center">
                                {logoUrl && (
                                    <div className="mr-3 flex items-center justify-center" style={{ width: '130px' }}>
                                        <img
                                            src={logoUrl}
                                            alt={`${fullName} Logo`}
                                            // className="h-auto w-auto object-contain"
                                            style={{ maxWidth: '130px', maxHeight: '60px' }}
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                        />
                                    </div>
                                )}
                                <span className="text-sm" style={{ lineHeight: '1' }}>
                                    {localStorage.getItem('ADDRESS')}
                                </span>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-between items-center pt-4 mt-4">
                                <div className="text-xs">
                                    {window.location.href}
                                </div>
                                <div className="text-xs">
                                    <span className="page-counter-display">1/1</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PrintPreview;